'use client';

import { useState } from 'react';
import type { Theme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import { SxProps } from '@mui/system/styleFunctionSx';
import { JucyLogo } from '@jucy-ui/components';
import { CTAButton } from '@/components/CTAButton';
import EnvRibbon from '@/components/EnvRibbon';
import { TopNavBar } from '@/components/NavBars/TopNavBar';
import { SiteContainer } from '@/components/SiteContainer';
import { useGetSitePrefix } from '@/hooks/useGetSitePrefix';
import { useSiteLinkPrefix } from '@/hooks/useSiteLinkPrefix';
import { CmsLink } from '@/services/cms/models/CmsLink';
import { CmsMenuItem } from '@/services/cms/models/CmsNavigationItem';
import { CmsSiteConfig } from '@/services/cms/models/CmsSiteConfig';
import { SiteAppBarMenu } from './SiteAppBarMenu';

export interface SiteAppBarProps {
    siteConfig: CmsSiteConfig;
    menuItems?: CmsMenuItem[];
}

export const SiteAppBar = ({ siteConfig, menuItems }: SiteAppBarProps) => {
    const [toolBarRef, setToolbarRef] = useState<HTMLDivElement | null>(null);
    const homeLink = useGetSitePrefix();
    return (
        <Box component="header">
            <AppBar component="div" sx={{ color: 'text.primary', borderTop: 'none' }}>
                <EnvRibbon />
                <TopNavBar siteConfig={siteConfig} />
                <SiteContainer>
                    <Toolbar sx={(theme) => ({ height: theme.settings.appBar.heights, position: 'relative' })} ref={(el) => setToolbarRef(el)} disableGutters>
                        <Link href={homeLink} sx={{ order: 1 }}>
                            <JucyLogo style={{ width: '133px', height: 'auto', marginRight: 16 }} />
                        </Link>
                        <SiteAppBarMenu siteConfig={siteConfig} container={toolBarRef || undefined} menuItems={menuItems} sx={{ order: { xs: 3, sm: 2 } }} />
                        <Stack direction={'row'} sx={{ ml: 'auto', gap: 2, order: { xs: 2, sm: 3 } }}>
                            <MenuBarCta link={siteConfig.siteConfig?.checkInLink} sx={{ display: { xs: 'none', sm: 'inline' } }} />
                            <MenuBarCta link={siteConfig.siteConfig?.bookNowLink} />
                        </Stack>
                    </Toolbar>
                </SiteContainer>
            </AppBar>
        </Box>
    );
};

type MenuBarCtaProps = {
    link?: CmsLink;
    sx?: SxProps<Theme>;
};
const MenuBarCta = ({ link, sx }: MenuBarCtaProps) => {
    const addSiteLinkPrefix = useSiteLinkPrefix();
    if (!link) {
        return null;
    }
    return (
        <CTAButton key={link.id} variant="contained" href={addSiteLinkPrefix(link.url)} target={link.openInNew ? '_blank' : undefined} sx={sx}>
            {link.title}
        </CTAButton>
    );
};
